import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Col, Row } from "reactstrap";
import { useAuth } from "../../context/AuthContext";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import Toast from "../../helpers/Toast";
import { StartAndEndExecutionDTO, StartAndEndExecutionDTOSchema } from "../../models/AssistanceExecution/StartAndEndExecutionDTO";
import { CountersDTO } from '../../models/Counter/CountersDTO';
import { CounterValueDTO } from '../../models/Counter/CounterValueDTO';
import { CountersFromEquipmentRequestDTO } from '../../models/Equipment/CountersFromEquipmentRequestDTO';
import { TimeHistoryDTO } from "../../models/TimeHistory/TimeHistoryDTO";
import { AssistanceService } from "../../services/AssistanceService";
import { EquipmentService } from '../../services/EquipmentService';
import { RenderCountersInputs } from "../Assistances/RenderCountersInputs";
import RenderEquipmentCountersComponent from '../Assistances/RenderEquipmentCountersComponent';
import ButtonComponent from "../ButtonComponent";
import InputComponent from "../InputComponent";
import { useMobile } from '../Layout/UseResponsive';
import MultiSelectComponent from "../MultiSelectComponent";
import { SelectItemsProps } from "../Select2Component";

type AssistanceExecutionsComponentType = {
	assistanceId: number;
	reloadExecutionsTable: () => void;
	isComplete: boolean;
	onFetchExecutionsData?: () => void;
	closeModalButtonIsVisible?: boolean;
	close?: () => void;
	equipmentId?: number;
}

export function ExecutionsInsertComponent({ assistanceId, reloadExecutionsTable, isComplete, equipmentId, ...props }: AssistanceExecutionsComponentType) {
	//#region Variables and States
	const assistanceService = new AssistanceService();
	const equipmentService = new EquipmentService();
	const intl = useIntl();
	const isMobile = useMobile();
	const [technicians, setTechnicians] = useState<SelectItemsProps[]>([]);
	const { currentUser, isAdmin, isAdministrative, isTechnician } = useAuth();
	const [selectedTechnicians, setSelectedTechnicians] = useState<string>(
		isTechnician() && currentUser?.id ? currentUser.id : ""
	);
	const today = new Date().toISOString().split('T')[0];
	const [day, setDay] = useState<string>(today);
	const [startTime, setStartTime] = useState<string>("");
	const [endTime, setEndTime] = useState<string>("");
	const [pending, setPending] = useState<string>("");
	const [workDone, setWorkDone] = useState<string | null>(null);
	const [isLoadingNewExecution, setIsLoadingNewExecution] = useState<boolean>(false);
	const [loadingCounters, setLoadingCounters] = useState<boolean>(true);
	const [errorCounters, setErrorCounters] = useState<string | null>(null);
	// Contadores e valores das últimas leituras
	const [counterValues, setCounterValues] = useState<CounterValueDTO[]>([]);
	// Leitura dos contadores para a nova execução
	const [newCounterValues, setNewCounterValues] = useState<CountersDTO[]>([]);

	// Add state variables for modules
	const { availableModules, counterOptions } = useAvailableModulesContext();
	const isActiveSimultaneousTimeRecording = availableModules.includes("SimultaneousTimeRecording");
	const isActiveCountersModule = availableModules.includes("IsActiveCountersModule");
	const isActiveErpSupportModule = availableModules.includes("IsActiveErpSupportModule");
	const isActiveWorkDoneAndPendingInExecutions = availableModules.includes("IsActiveWorkDoneAndPendingInExecutions");
	const isActiveEquipmentCountersModule = availableModules.includes("IsActiveEquipmentCountersModule");
	const [localTimeHistory, setLocalTimeHistory] = useState<TimeHistoryDTO>({ counter1: 0, counter2: 0, counter3: 0 } as TimeHistoryDTO);
	const isMultiSelectDisabled =
		isComplete ||
		isLoadingNewExecution ||
		(!isAdmin() && !isAdministrative() && !isActiveSimultaneousTimeRecording);
	//#endregion

	const getTechnicians = async () => {
		const list: SelectItemsProps[] = [];
		setIsLoadingNewExecution(true);
		var response = await assistanceService.GetWorkshopTechnicians();
		setIsLoadingNewExecution(false);

		if (response.success === false) {
			Toast.Show("error", response.message);
			return;
		}

		response.obj.forEach((element) => {
			var item: SelectItemsProps = {
				value: element.id,
				label: element.name,
			};
			list.push(item);
		});

		setTechnicians(list);
	};

	const addNewExecution = async () => {
		const executionInfo: StartAndEndExecutionDTO = {
			executionStart: new Date(`${day}T${startTime}`),
			executionEnd: new Date(`${day}T${endTime}`),
			assistanceId: assistanceId,
			technicianId: selectedTechnicians,
			pending: pending,
			workDone: workDone,
		};

		// Add counter values to the execution data when both modules are active
		if (isActiveCountersModule && isActiveErpSupportModule) {
			// Use CountersDTO to pass counter values, converting undefined to 0
			executionInfo.counters = newCounterValues.map(counter => ({
				...counter,
				counterValue: counter.counterValue ?? 0
			}));
		}

		var responseValidate = StartAndEndExecutionDTOSchema.validate(executionInfo, { allowUnknown: true });
		if (responseValidate.error != null) {
			var message = responseValidate.error!.message;
			Toast.Show("error", message);
			return false;
		}

		setIsLoadingNewExecution(true);
		var response = await assistanceService.AddExecutionBackoffice(executionInfo);
		setIsLoadingNewExecution(false);

		if (response.success !== true || response.obj == null) {
			Toast.Show("error", response.message);
			return;
		}

		Toast.Show("success", "Execução inserida com sucesso");

		// Call onFetchExecutionsData to update parent with new data including counters
		if (props.onFetchExecutionsData) {
			props.onFetchExecutionsData();
		}

		fetchCounters();
		reloadExecutionsTable();
		resetInputsFields();
	};

	const resetInputsFields = () => {
		setDay(today);
		setStartTime("");
		setEndTime("");
		setPending("");
		setWorkDone("");
		if (newCounterValues.length > 0) {
			const resetCounters = newCounterValues.map(counter => ({
				...counter,
				counterValue: undefined
			}));
			setNewCounterValues(resetCounters);
		}
	}

	const fetchCounters = async () => {
		setLoadingCounters(true);
		setErrorCounters(null);

		try {
			// Create request parameters to get all counters for this equipment
			const request: CountersFromEquipmentRequestDTO = {
				equipmentId: equipmentId ?? 0,
				currentPage: 1,
				pageSize: 100 // Get all counters
			};

			const response = await equipmentService.GetCountersFromEquipment(request);

			if (response.success) {
				// Initialize counter values based on fetched data
				const initialCounterValues: CounterValueDTO[] = response.items.map(counter => ({
					id: counter.id,
					value: counter.value,
					unit: counter.unit || '',
					name: counter.name,
					code: counter.code
				}));

				const initializedCounters = response.items.map(counter => ({
					counterName: `${counter.code}-${counter.name}`,
					counterValue: undefined
				}));
				setLoadingCounters(false);
				setCounterValues(initialCounterValues);
				setNewCounterValues(initializedCounters);
			} else {
				setLoadingCounters(false);
				setErrorCounters(response.message || "Failed to fetch counters");
			}
		} catch (err) {
			setLoadingCounters(false);
			setErrorCounters("An error occurred while fetching equipment counters");
			console.error(err);
		} finally {
			setLoadingCounters(false);
		}
	};

	useEffect(() => {
		if (equipmentId) {
			fetchCounters();
		}
	}, [equipmentId]);

	useEffect(() => {
		if (assistanceId !== 0) {
			getTechnicians();
		}
	}, [assistanceId])

	return (
		<div>
			<Row style={{ margin: isMobile ? "1rem 0 0" : "1rem 0px" }}>
				<MultiSelectComponent
					xl={8}
					lg={8}
					md={8}
					sm={12}
					xs={12}
					style={{ width: "100%" }}
					label={"Técnico"}
					items={technicians}
					setSelectedString={setSelectedTechnicians}
					selectString={selectedTechnicians}
					isDisabled={isMultiSelectDisabled}
				/>

				<InputComponent xl={4} lg={4} md={4} sm={12} xs={12}
					style={{ width: "100%" }}
					type={"date"}
					value={day}
					label={"Dia"}
					readOnly={isComplete === false ? isLoadingNewExecution : true}
					showLoadingSkeleton={isLoadingNewExecution}
					onChange={(t: string) => {
						setDay(t);
					}}
				/>

				<InputComponent xl={4} lg={4} md={4} sm={6} xs={6}
					style={{ width: "100%" }}
					type={"time"}
					value={startTime}
					label={"Hora inicio"}
					readOnly={isComplete === false ? isLoadingNewExecution : true}
					showLoadingSkeleton={isLoadingNewExecution}
					onChange={(t: string) => {
						setStartTime(t);
					}}
				/>

				<InputComponent xl={4} lg={4} md={4} sm={6} xs={6}
					style={{ width: "100%" }}
					type={"time"}
					value={endTime}
					label={"Hora Fim"}
					readOnly={isComplete === false ? isLoadingNewExecution : true}
					showLoadingSkeleton={isLoadingNewExecution}
					onChange={(t: string) => {
						setEndTime(t);
					}}
				/>

				{isActiveWorkDoneAndPendingInExecutions &&
					<>
						<InputComponent
							xl={6}
							lg={6}
							md={6}
							sm={12}
							xs={12}
							style={{ width: "100%" }}
							type={"textArea"}
							value={workDone}
							label={"Trabalho Efetuado"}
							required={true}
							readOnly={isComplete}
							showLoadingSkeleton={false}
							onChange={(t: string) => {
								setWorkDone(t);
							}}
						/>

						<InputComponent
							xl={6}
							lg={6}
							md={6}
							sm={12}
							xs={12}
							style={{ width: "100%" }}
							type={"textArea"}
							value={pending}
							label={"Trabalho Pendente"}
							required={false}
							readOnly={isComplete}
							showLoadingSkeleton={false}
							onChange={(t: string) => {
								setPending(t);
							}}
						/>
					</>
				}
			</Row>
			{isActiveCountersModule && isActiveErpSupportModule && isActiveWorkDoneAndPendingInExecutions && (
				<>
					<label style={{ margin: "0px", padding: "5px 0 5px 12px", fontWeight: "bold" }}>
						{intl.formatMessage({ id: "Screens.AssistanceEditB.CountersTitle" })}
					</label>
					<Row style={{ margin: "0px", padding: "0px" }}>
						{isActiveEquipmentCountersModule ?
							<>
								<RenderEquipmentCountersComponent
									isComplete={isComplete}
									counterValues={counterValues}
									newCounterValues={newCounterValues}
									setNewCounterValues={setNewCounterValues}
									loadingCounters={loadingCounters}
									errorCounters={errorCounters}
								/>
							</>
							:
							<>
								{RenderCountersInputs(counterOptions, localTimeHistory, setLocalTimeHistory, isComplete)}
							</>
						}
					</Row>
				</>
			)
			}

			<Row style={{ justifyContent: "flex-end", margin: "1rem 0px" }}>
				<Col xl={4} lg={4} md={4} sm={12} xs={12}>
					<ButtonComponent
						style={{ width: '100%' }}
						onClick={addNewExecution}
						text={intl.formatMessage({ id: "ExecutionsInsertComponent.Insert" })}
						loading={isLoadingNewExecution}
						disabled={isComplete === false ? isLoadingNewExecution : true}
					/>
				</Col>
				{props.closeModalButtonIsVisible &&
					<Col xl={4} lg={4} md={4} sm={4} xs={4}>
						<ButtonComponent
							style={{ width: '100%' }}
							onClick={() => props.close && props.close()}
							text={intl.formatMessage({ id: "ChangeStatusComponent.Cancel" })}
							loading={isLoadingNewExecution}
							disabled={isComplete === false ? isLoadingNewExecution : true}
						/>
					</Col>
				}
			</Row>
		</div >
	)
}

